import React, { useEffect, useState } from 'react'
import api from '../../../helpers/apiRequester'
import { EmptyState, StatusPill } from '../../../ui'
import type { Visit, VisitCall } from './types'
import { formatDuration, formatTimeWithSeconds } from './format'
import styles from './styles.module.scss'

interface VisitCallsProps {
    cognitoId: string
    visit: Visit
}

const statusTone = (status: number | null) => {
    if (status == null) return 'neutral' as const
    if (status >= 500) return 'bad' as const
    if (status >= 400) return 'warn' as const
    return 'good' as const
}

/** The API calls made during one visit, loaded when the row is expanded. */
export const VisitCalls = ({ cognitoId, visit }: VisitCallsProps) => {
    const [calls, setCalls] = useState<VisitCall[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [openParams, setOpenParams] = useState<Set<number>>(new Set())

    useEffect(() => {
        let active = true
        setCalls(null)
        setError(null)
        api.post('activity-visit-calls', { cognitoId, page: visit.page, from: visit.at, to: visit.until })
            .then(({ data }) => {
                if (!active) return
                if (data?.success) setCalls(data.calls || [])
                else setError(data?.message || 'Could not load requests')
            })
            .catch((err: any) => {
                if (active) setError(err?.response?.data?.message || err?.message || 'Could not load requests')
            })
        return () => {
            active = false
        }
    }, [cognitoId, visit.page, visit.at, visit.until])

    const toggleParams = (id: number) =>
        setOpenParams((current) => {
            const next = new Set(current)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })

    return (
        <div className={styles.calls}>
            <div className={styles.callsHead}>
                Requests during this visit{calls ? ` (${calls.length})` : ''}
            </div>
            {error && <EmptyState bare tone="error" title={error} />}
            {!error && calls === null && <EmptyState bare tone="loading" title="Loading requests…" />}
            {!error && calls && calls.length === 0 && (
                <EmptyState bare title="No requests recorded" description="The page did not call the API while it was open." />
            )}
            {calls?.map((call) => {
                const hasParams = call.params && Object.keys(call.params).length > 0
                return (
                    <React.Fragment key={call.id}>
                        <div className={styles.call}>
                            <span className={styles.time}>{formatTimeWithSeconds(call.at)}</span>
                            <span className={styles.callPath} title={call.path}>
                                {call.path}
                                {hasParams && (
                                    <>
                                        {' '}
                                        <button type="button" className={styles.paramsToggle} onClick={() => toggleParams(call.id)}>
                                            {openParams.has(call.id) ? 'hide params' : 'show params'}
                                        </button>
                                    </>
                                )}
                            </span>
                            <StatusPill size="sm" tone={statusTone(call.status)}>
                                {call.status ?? '—'}
                            </StatusPill>
                            <span className={styles.callNum}>{formatDuration(call.durationMs)}</span>
                            <span className={styles.callNum} title="Result count">
                                {call.resultCount != null ? `${call.resultCount} rows` : ''}
                            </span>
                            {openParams.has(call.id) && hasParams && (
                                <pre className={styles.params}>{JSON.stringify(call.params, null, 2)}</pre>
                            )}
                        </div>
                    </React.Fragment>
                )
            })}
        </div>
    )
}
